import type React from "react";
import { useState } from "react";

import { faReceipt, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack } from "@mui/material";
import {
  type GridCsvExportOptions,
  type GridCsvGetRowsToExportParams,
  gridExpandedSortedRowIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import { useGetInvoiceSpecification } from "api/services/admin/hooks";
import type { Invoice, InvoiceSpecificationRow } from "api/services/admin/schemas";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";
import { WidgetBox } from "shared/containers/WidgetBox/WidgetBox";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { convertCentsToDollarsNumber } from "utils/convertCentsToDollars";
import { useLocalizedDate } from "utils/date/useLocalizedDate";
import { translateCurrency } from "utils/translations/translateCurrency";

const getFilteredRows = ({ apiRef }: GridCsvGetRowsToExportParams) => gridExpandedSortedRowIdsSelector(apiRef);

const formatCents = (cents: number, currency: string) =>
  translateCurrency(convertCentsToDollarsNumber(Math.round(cents)), currency);

interface Props {
  invoice: Invoice;
  hcoID: number;
  onClose: () => void;
}

export const InvoicePreviewModal: React.FC<Props> = ({ invoice, hcoID, onClose }) => {
  const { t } = useTranslation();
  const { parseISO, format } = useLocalizedDate();
  const apiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const handleExport = (options: GridCsvExportOptions) => apiRef.current.exportDataAsCsv(options);
  const csvFilename = `invoice_specification_${invoice.invoice_id}`;

  const { data, isLoading } = useGetInvoiceSpecification(
    { health_care_organization_id: hcoID, invoice_id: invoice.invoice_id },
    { enabled: true }
  );

  const title = invoice.billing_period ? format(parseISO(invoice.billing_period), "MMMM yyyy") : invoice.invoice_id;

  const rows = data?.rows ?? [];

  const columns: GridColDef<InvoiceSpecificationRow>[] = [
    {
      field: "patient_name",
      headerName: t("admin.invoice_specification.patient_name"),
      flex: 1.5,
    },
    {
      field: "cpt_code",
      headerName: t("admin.invoice_specification.cpt_code"),
      flex: 1,
    },
    {
      field: "date_of_service",
      headerName: t("admin.invoice_specification.date_of_service"),
      flex: 1,
      valueFormatter: (value: string) => (value ? format(parseISO(value), "P") : "-"),
    },
    {
      field: "claim_value_cents",
      headerName: t("admin.invoice_specification.claim_value"),
      flex: 1,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value: number, row) => formatCents(value, row.currency),
    },
    {
      field: "fee_cents",
      headerName: t("admin.invoice_specification.fee"),
      flex: 1,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value: number, row) => formatCents(value, row.currency),
    },
  ];

  return (
    <MUIUnifiedModal open showCloseButton={false} onClose={onClose}>
      <WidgetBox
        title={title}
        description={
          rows.length === 1
            ? t("admin.invoice_specification.row_count_one")
            : t("admin.invoice_specification.row_count", { count: rows.length })
        }
        icon={<FontAwesomeIcon icon={faReceipt} />}
        color="blue"
        toolbar={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BaseButton
              variant="action"
              onClick={() =>
                handleExport({ getRowsToExport: getFilteredRows, fileName: csvFilename, allColumns: true })
              }
              icon="arrow-down-from-bracket"
              text={t("buttons.export_csv")}
            />
            <BaseButton variant="secondaryLink" onClick={onClose}>
              <FontAwesomeIcon icon={faXmark} />
            </BaseButton>
          </Stack>
        }
      >
        <MUIDataGrid
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          getRowId={row => `${row.patient_name}-${row.date_of_service}-${row.fee_cents}`}
          loading={isLoading}
          paginationModel={paginationModel}
          transparentBackground
          onPaginationModelChange={model => setPaginationModel(model)}
          rowSelection={false}
          slots={{
            noRowsOverlay: () => <EmptyResultOverlay />,
            noResultsOverlay: () => <EmptyResultOverlay />,
          }}
          localeText={{
            noRowsLabel: "",
            noResultsOverlayLabel: "",
          }}
          disableColumnMenu
          hideHeader
          density="comfortable"
          sx={{
            "& .MuiDataGrid-row": {
              cursor: "default",
            },
          }}
        />
      </WidgetBox>
    </MUIUnifiedModal>
  );
};
